import { useState } from "react";
import { Mail, Send, Crown } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState({ type: "idle", msg: "" });

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ type: "loading", msg: "" });
        try {
            await api.post("/newsletter", { email });
            setStatus({ type: "ok", msg: "Sealed. The decree will reach you." });
            setEmail("");
        } catch (err) {
            setStatus({
                type: "err",
                msg: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        }
    };

    return (
        <footer
            data-testid="site-footer"
            className="relative border-t mt-32"
            style={{ borderColor: "rgba(212,175,55,0.15)", background: "#070707" }}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                <div className="grid md:grid-cols-12 gap-16">
                    <div className="md:col-span-5">
                        <div className="eyebrow mb-4">The Sovereign Decree</div>
                        <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                            New chapters arrive in <span className="text-gold-gradient">silence</span>.
                            <br />
                            We tell you first.
                        </h3>
                        <p className="text-[#A1A1AA] mt-4 max-w-md">
                            Receive a single dispatch each fortnight — chapter releases, behind-the-panel
                            studio notes, and collector-edition pre-orders.
                        </p>

                        <form
                            onSubmit={submit}
                            data-testid="newsletter-form"
                            className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md"
                        >
                            <div
                                className="flex items-center gap-3 flex-1 px-4 bg-[#141414] border"
                                style={{ borderColor: "rgba(212,175,55,0.2)" }}
                            >
                                <Mail className="w-4 h-4 text-[#D4AF37]" />
                                <input
                                    data-testid="newsletter-email"
                                    type="email"
                                    required
                                    placeholder="your@scroll.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="bg-transparent flex-1 py-3 text-sm text-white placeholder:text-[#5a5a5a] outline-none border-0"
                                />
                            </div>
                            <button
                                data-testid="newsletter-submit"
                                type="submit"
                                disabled={status.type === "loading"}
                                className="btn-gold disabled:opacity-50"
                            >
                                {status.type === "loading" ? "Sealing…" : "Subscribe"}
                                <Send className="w-3.5 h-3.5" />
                            </button>
                        </form>
                        {status.msg && (
                            <p
                                data-testid="newsletter-status"
                                className={`mt-3 text-xs uppercase tracking-widest ${
                                    status.type === "err" ? "text-[#cd6a6a]" : "text-[#D4AF37]"
                                }`}
                            >
                                {status.msg}
                            </p>
                        )}
                    </div>

                    <div className="md:col-span-3 md:col-start-8">
                        <div className="eyebrow mb-4">EIGOO Books</div>
                        <ul className="space-y-2.5 text-sm text-[#A1A1AA]">
                            <li className="hover:text-[#E6D59A] transition-colors">
                                <a href="/publish">Publish With Us</a>
                            </li>
                            <li className="hover:text-[#E6D59A] transition-colors">
                                <a href="/apply">Writer application</a>
                            </li>
                            <li className="hover:text-[#E6D59A] transition-colors">
                                <a href="/library">The Library</a>
                            </li>
                            <li className="hover:text-[#E6D59A] transition-colors">
                                <a href="/comic/sovereign-commander">Sovereign Commander</a>
                            </li>
                        </ul>
                    </div>
                    <div className="md:col-span-2">
                        <div className="eyebrow mb-4">EIGOO Inc.</div>
                        <ul className="space-y-2.5 text-sm text-[#A1A1AA]">
                            <li className="hover:text-[#E6D59A] transition-colors">
                                <a href="https://eigooinc.net" target="_blank" rel="noopener noreferrer">
                                    eigooinc.net
                                </a>
                            </li>
                            <li className="hover:text-[#E6D59A] transition-colors">
                                <a
                                    href="https://legal.eigooinc.net/terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-terms-link"
                                >
                                    Terms
                                </a>
                            </li>
                            <li className="hover:text-[#E6D59A] transition-colors">
                                <a
                                    href="https://legal.eigooinc.net/privacy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-privacy-link"
                                >
                                    Privacy
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="gold-rule mt-16" />
                <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <Crown className="w-4 h-4 text-[#D4AF37]" />
                        <span className="font-serif text-white text-lg">EIGOO Books</span>
                        <span className="text-[#5a5a5a] text-xs">·</span>
                        <span className="text-[#5a5a5a] text-xs uppercase tracking-widest">
                            Publishing · Distribution · Marketing
                        </span>
                    </div>
                    <div
                        className="flex items-center gap-4 text-[#5a5a5a] text-xs flex-wrap"
                        data-testid="legal-shield"
                    >
                        <a
                            href="https://legal.eigooinc.net/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#E6D59A]"
                        >
                            Terms
                        </a>
                        <span>·</span>
                        <a
                            href="https://legal.eigooinc.net/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#E6D59A]"
                        >
                            Privacy
                        </a>
                        <span>·</span>
                        <a
                            href="https://legal.eigooinc.net"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#E6D59A]"
                            data-testid="legal-shield-link"
                        >
                            Powered by EIGOO Legal Shield
                        </a>
                    </div>
                </div>
                <p className="text-[#5a5a5a] text-xs mt-4 md:text-right">
                    © {new Date().getFullYear()} EIGOO Books · an EIGOO Inc. company.
                </p>
            </div>
        </footer>
    );
}
